import type { MouseEvent, ReactNode } from 'react'

import { MultipleChoice, platformService } from '@monthio/react-components'
import React, { useCallback, useEffect, useState } from 'react'
import { Translation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import styled from 'styled-components'

import type { RootState } from '../../../store/reducers'
import type { BudgetHelperQuestionnaireInputs } from '../../../types'

import { CivilStatus } from '../../../types'
import {
	Alternative,
	MultipleChoiceWrapper,
	useStyles,
} from '../../BudgetHelper/steps/StepMultipleChoice'
import { AloneSameDeviceStart } from './AloneSameDeviceStart'
import { StartFlow } from './StartFlow'

interface Choice {
	label: ReactNode
	value: string
}

const Wrapper = styled.div`
	display: flex;
	flex-flow: column nowrap;
	align-items: center;
	width: 100%;
`

const StyledMultipleChoice = styled(MultipleChoice)`
	width: 100%;
`

const Spacer = styled.div`
	margin-bottom: 32px;
`

const pairingChoices: Readonly<Choice>[] = [
	{
		label: (
			<Translation>
				{(t) => t('genericLandingPage-paringChoice-alone')}
			</Translation>
		),
		value: 'alone',
	},
	{
		label: (
			<Translation>
				{(t) => t('genericLandingPage-paringChoice-couple')}
			</Translation>
		),
		value: 'couple',
	},
]
export type PairingChoice = 'alone' | 'couple'

const flowChoices: Readonly<Choice>[] = [
	{
		label: (
			<Translation>
				{(t) => t('genericLandingPage-flowChoice-sameDevice')}
			</Translation>
		),
		value: 'sameDevice',
	},
	{
		label: (
			<Translation>
				{(t) => t('genericLandingPage-flowChoice-differentDevices')}
			</Translation>
		),
		value: 'differentDevices',
	},
]
export type FlowChoice = 'sameDevice' | 'differentDevices'

export const ParingChoiceSelector = ({
	clientInputs,
}: {
	clientInputs?: BudgetHelperQuestionnaireInputs
}) => {
	const classes = useStyles()
	const [pairingChoice, setPairingChoice] = useState<PairingChoice | null>(null)
	const [flowChoice, setFlowChoice] = useState<FlowChoice | null>(null)
	const {
		canCreatePairedCases,
		canRunPairedCasesOnSameDevice,
		regionId,
		clientId,
	} = useSelector(
		(state: RootState) => ({
			canCreatePairedCases:
				state.flowEntryStatemachine.whitelabelSubdomainInfo
					?.canCreatePairedCases,
			canRunPairedCasesOnSameDevice:
				state.flowEntryStatemachine.whitelabelSubdomainInfo
					?.canRunPairedCasesOnSameDevice,
			regionId: state.flowEntryStatemachine.whitelabelSubdomainInfo?.region,
			clientId: state.flowEntryStatemachine.whitelabelSubdomainInfo?.clientId,
		}),
		shallowEqual,
	)

	const environment = platformService.getEnvironment()

	//if Dansk Bank likes this, or any other clients want this, this should made as a feature on whitelabel-config
	const isDanskeBankSweden =
		regionId === 'se' &&
		((environment === 'test' && clientId === 292) ||
			(environment === 'production' && clientId === 234))

	useEffect(() => {
		if (
			clientInputs?.civilStatus === CivilStatus.Alone ||
			clientInputs?.mergedFinances === Alternative.NO
		) {
			setPairingChoice('alone')
			return
		}
		if (clientInputs?.civilStatus === CivilStatus.Married) {
			setPairingChoice('couple')
		}
	}, [clientInputs?.civilStatus])

	const onPairingChoiceClick = useCallback(
		(e: MouseEvent<HTMLInputElement, PairingChoice>) => {
			setPairingChoice(e.currentTarget.value as PairingChoice)
			if (isDanskeBankSweden) {
				setFlowChoice('sameDevice')
			}
		},
		[],
	)
	const onFlowChoiceClick = useCallback(
		(e: MouseEvent<HTMLInputElement, FlowChoice>) => {
			setFlowChoice(e.currentTarget.value as FlowChoice)
		},
		[],
	)

	if (canCreatePairedCases === false) {
		return <AloneSameDeviceStart clientInputs={clientInputs} />
	}

	return (
		<Wrapper>
			{clientInputs?.civilStatus ? null : (
				<MultipleChoiceWrapper $columns={2}>
					<StyledMultipleChoice
						className={`${classes.root} ${classes.button}`}
						choices={pairingChoices}
						checked={pairingChoice as string}
						onClick={onPairingChoiceClick}
						fieldName="pairingChoice"
						choiceAlign="center"
					/>
				</MultipleChoiceWrapper>
			)}
			{canRunPairedCasesOnSameDevice &&
			pairingChoice === 'couple' &&
			!isDanskeBankSweden ? (
				<>
					<Spacer />
					<MultipleChoiceWrapper $columns={2}>
						<StyledMultipleChoice
							className={`${classes.root} ${classes.button}`}
							choiceAlign="center"
							choices={flowChoices}
							checked={flowChoice as string}
							onClick={onFlowChoiceClick}
							fieldName="flowChoice"
						/>
					</MultipleChoiceWrapper>
				</>
			) : null}
			<Spacer />
			<StartFlow
				flowChoice={
					canRunPairedCasesOnSameDevice ? flowChoice : 'differentDevices'
				}
				pairingChoice={pairingChoice}
				clientInputs={clientInputs}
			/>
		</Wrapper>
	)
}
